/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { announce, clearAnnouncer } from '@react-aria/live-announcer';

import type { InlineNotificationProps } from '@coursera/cds-core';
import { InlineNotification } from '@coursera/cds-core';

export type NotificationMessage = {
  content: string;
  severity: InlineNotificationProps['severity'];
  isTransient?: boolean;
};

export const TRANSIENT_DURATION_MS = 3000;
// for durations longer than this, consider using a non-transient notification instead
export const TRANSIENT_DURATION_LONG_MS = 6000;

type NotificationContextType = {
  addNotification: (message: NotificationMessage) => void;
  clearNotification: () => void;
};
const NotificationContext = React.createContext<NotificationContextType>({
  addNotification: () => undefined,
  clearNotification: () => undefined,
});

const styles = {
  notifications: css`
    margin: 0 auto;
    left: var(--cds-spacing-200);
    right: var(--cds-spacing-200);
    opacity: 1;

    > div {
      box-shadow: 0 6px 24px 0 var(--cds-color-darken-100);
      animation: var(--labs-animation-fade-in);
      opacity: 1;
    }
  `,
};

/** Hook to add an inline notification to any setting page */
export const useSettingsNotification = () => useContext(NotificationContext);

/**
 * Helper to render notification inside a portal but located relative to the containerRef
 */
const NotificationPortal = ({
  children,
  containerRef,
}: React.PropsWithChildren<{ containerRef: React.RefObject<HTMLElement> }>) => {
  const [portalContainer] = useState(() => document.createElement('div'));

  useEffect(() => {
    document.body.appendChild(portalContainer);
    return () => {
      document.body.removeChild(portalContainer);
      clearAnnouncer('assertive');
    };
  }, [portalContainer]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const boundingRect = containerRef.current.getBoundingClientRect();
      portalContainer.style.position = 'fixed';
      portalContainer.style.top = `${boundingRect.top + 64}px`; // top offset from reference element
      portalContainer.style.left = `${boundingRect.left + boundingRect.width / 2}px`; // center horizontally
      portalContainer.style.transform = 'translateX(-50%)'; // center horizontally
      portalContainer.style.width = `${boundingRect.width}px`;
      portalContainer.style.maxWidth = '400px';
      portalContainer.style.padding = '0 var(--cds-spacing-200)';
      portalContainer.style.zIndex = '1000001'; // same as floating ui z-index
    }
  }, [portalContainer, containerRef]);

  return ReactDOM.createPortal(children, portalContainer);
};

export const SettingsNotificationProvider = ({ children }: React.PropsWithChildren) => {
  const [notification, setNotification] = useState<NotificationMessage | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const clearNotification = useCallback<() => void>(() => {
    setNotification(null);
    clearAnnouncer('assertive');
  }, []);

  const handleAnnounce = useCallback<(text: string) => void>((text: string) => {
    clearAnnouncer('assertive');
    announce(text, 'assertive');
  }, []);

  const addNotification = useCallback<(notif: NotificationMessage) => void>(
    (notif: NotificationMessage) => {
      setNotification(notif);

      handleAnnounce(notif.content);

      if (notif.isTransient) {
        // set transient duration dynamically based on content length being shown for better readability
        // on average, 40 characters roughly equates to 7 words which is read on average in under 3 seconds
        const duration = notif.content.length > 40 ? TRANSIENT_DURATION_LONG_MS : TRANSIENT_DURATION_MS;
        setTimeout(() => {
          clearNotification();
        }, duration);
      }
    },
    [clearNotification, handleAnnounce]
  );

  const handleDismiss = useCallback<() => void>(() => {
    clearNotification();
    // bring focus to container after dismiss,
    // since the Dismiss button that previously had focus is now removed from DOM
    containerRef.current?.focus();
  }, [clearNotification]);

  return (
    <NotificationContext.Provider value={{ addNotification, clearNotification }}>
      <div ref={containerRef} tabIndex={-1}>
        {children}
      </div>

      {notification ? (
        <NotificationPortal containerRef={containerRef}>
          <div css={styles.notifications}>
            <InlineNotification
              severity={notification.severity}
              onDismiss={notification.isTransient ? undefined : handleDismiss}
            >
              {notification.content}
            </InlineNotification>
          </div>
        </NotificationPortal>
      ) : null}
    </NotificationContext.Provider>
  );
};
